import { customDateFormatter } from '../../lib';
import type {
  ccModel,
  icModel,
  infrastructurePortalUtil
} from 'generated-ts-common';

type OrganizationalUnit = infrastructurePortalUtil.saf.infrastructure.model.hpd.OrganizationalUnit;
type ParticipantContainer = icModel.saf.infrastructure.model.appointments.ParticipantContainer;
type HealthCareProvider = icModel.saf.infrastructure.model.hpd.HealthCareProvider;
type GenCode = infrastructurePortalUtil.saf.infrastructure.model.svs.GenCode;
type AppointmentSchedulingServiceType = ccModel.portal.config.appointments.AppointmentSchedulingServiceType;

type Appointment = icModel.saf.infrastructure.model.appointments.Appointment;

export enum ProviderTypeEnum {
  INDIVIDUAL = 'INDIVIDUAL',
  ORGANIZATION = 'ORGANIZATION'
}

export enum ParticipantType {
  PRACTITIONER = 'Practitioner',
  LOCATION = 'Location',
  PRIMARY_PERFORMER = 'PPRF'
}

export enum BackServiceTypeEnum {
  SKED24 = 'SKED24',
  HDR = 'HDR'
}

export enum AppointmentStatusEnum {
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING',
  PROPOSED = 'PROPOSED',
  BOOKED = 'BOOKED'
}

export const AppointmentLocationEnum = {
  VV_LOCATION: 'VirtualVisitScheduleLocationId'
} as const;

export const formattedTimeFromAppointment = (
  appointmentStart?: string
): string =>
  customDateFormatter({
    minute: 'numeric',
    hour: 'numeric',
    hour12: false
  }).formatDate(appointmentStart || '');

export const splitName = (
  name?: string
): { firstName: string; lastName: string } => {
  let firstName = '';
  let lastName = '';
  if (name) {
    const spacePos = name.indexOf(' ');
    if (spacePos >= 0) {
      firstName = name.substring(0, spacePos);
    }
    lastName = name.substring(firstName.length);
  }
  return { lastName: lastName.trim(), firstName: firstName.trim() };
};

export const getAppointentParticipant = (
  providerType: OrganizationalUnit,
  participants?: ParticipantContainer[]
): HealthCareProvider | undefined =>
  participants?.find(item => item.participant.providerType === providerType)
    ?.participant;

export function getAppointmentResourceAndCenter(
  participants: ParticipantContainer[],
  isSked: boolean
): Array<HealthCareProvider | undefined> {
  let resource, center;
  if (isSked) {
    resource = getAppointentParticipant(
      ProviderTypeEnum.INDIVIDUAL,
      participants
    );
    center = getAppointentParticipant(
      ProviderTypeEnum.ORGANIZATION,
      participants
    );
  } else {
    resource = participants.find(
      p => p.participantType === ParticipantType.PRIMARY_PERFORMER
    )?.participant;
    if (!resource) {
      resource = participants.find(
        p => p.participantType === ParticipantType.PRACTITIONER
      )?.participant;
    }
    center = participants.find(
      p => p.participantType === ParticipantType.LOCATION
    )?.participant;
  }

  return [resource, center];
}

export const getServiceTypeFromAppointment = (
  appointment?: Appointment
): GenCode | undefined =>
  appointment?.serviceType && appointment.serviceType.length > 0
    ? appointment.serviceType[0]
    : undefined;

export const getSpecialtyFromAppointment = (
  appointment?: Appointment
): GenCode | undefined =>
  appointment?.specialties && appointment.specialties.length > 0
    ? appointment.specialties[0]
    : undefined;

export const isBackendServiceSked = (
  backendService: AppointmentSchedulingServiceType
): boolean => backendService === BackServiceTypeEnum.SKED24;

export const formatTimeForScreenReader = (date: Date): string => {
  const locale = window.SenseConstants?.locale ?? 'en-US';
  const minute = date.getMinutes();
  // Let Intl handle day-period placement natively instead of manually splicing parts.
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    ...(minute !== 0 && { minute: '2-digit' })
  }).format(date);
};

export const formatDateForScreenReader = (date: Date): string => {
  const locale = window.SenseConstants?.locale ?? 'en-US';
  return `${new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(
    date
  )} ${new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date)}`;
};
